import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  FileTextIcon,
  InfoIcon,
  ShieldCheckIcon,
  StethoscopeIcon,
  UsersIcon
} from 'lucide-react';
import { TextField } from '../components/Field';
import { Turnstile } from '../components/Turnstile';
import { usePortal, toResearcher } from '../contexts/PortalContext';
import * as api from '../api/research';
import { INSTITUTION, IRB_EMAIL } from '../data/proposalContent';
import { homeFor } from '../data/roles';

const EMAIL_OR_MOBILE = /^([^\s@]+@[^\s@]+\.[^\s@]+|[0-9+\-\s()]{7,20})$/;

type Mode = 'signin' | 'confirm' | 'forgot' | 'reset';

export function SignIn() {
  const { adoptSession, config } = usePortal();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('signin');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pendingToken, setPendingToken] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [captcha, setCaptcha] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [captchaBroken, setCaptchaBroken] = useState(false);
  const siteKey = config?.turnstileSiteKey ?? '';
  const irbEmail = config?.irbEmail ?? IRB_EMAIL;

  const resetCaptcha = () => setCaptchaNonce((n) => n + 1);

  /* Only hold the form back while the widget is working and simply has not been
     completed. If it could not load, the server is still the authority. */
  const guardCaptcha = (): boolean => {
    if (siteKey && !captcha && !captchaBroken) {
      setFailure('Please complete the security check.');
      return false;
    }
    return true;
  };

  /* ------------------------------------------------------------- sign in */

  const handleSignIn = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!loginId.trim()) next.loginId = 'Enter your registered email address or mobile number.';
    else if (!EMAIL_OR_MOBILE.test(loginId.trim()))
      next.loginId = 'Enter a valid email address or mobile number.';
    if (!password) next.password = 'Enter your password.';
    setErrors(next);
    setFailure(null);
    if (Object.keys(next).length > 0) return;
    if (!guardCaptcha()) return;

    setBusy(true);
    try {
      const res = await api.login({
        LoginId: loginId.trim(),
        Password: password,
        TurnstileToken: captcha
      });

      if (res.ok && res.sessionToken && res.profile) {
        adoptSession(res.sessionToken, toResearcher(res.profile));
        navigate(homeFor(res.profile), { replace: true });
        return;
      }

      /* Correct password, unconfirmed address — the API has already sent a
         fresh code, so go straight to the confirmation step. */
      if (res.pendingToken) {
        setPendingToken(res.pendingToken);
        setMode('confirm');
        setNotice(res.message);
        setFailure(null);
      } else {
        setFailure(res.message || 'Could not sign you in.');
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not sign you in.');
    } finally {
      setBusy(false);
      resetCaptcha();
    }
  };

  /* --------------------------------------------------- confirm / reset pin */

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailure(null);
    if (pin.trim().length < 4) {
      setErrors({ pin: 'Enter the 6-digit code from your email.' });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      const res = await api.verifyPin({ PendingToken: pendingToken, Pin: pin.trim() });
      if (res.ok && res.sessionToken && res.profile) {
        adoptSession(res.sessionToken, toResearcher(res.profile));
        navigate(homeFor(res.profile), { replace: true });
        return;
      }
      if (res.ok) {
        /* A reset code was accepted — move on to choosing a new password. */
        setMode('reset');
        setNotice(res.message);
        setPin(pin.trim());
      } else {
        setFailure(res.message || 'That code was not accepted.');
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'That code was not accepted.');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setFailure(null);
    if (!guardCaptcha()) return;
    setBusy(true);
    try {
      const res = await api.resendPin({ PendingToken: pendingToken, TurnstileToken: captcha });
      if (res.ok && res.pendingToken) setPendingToken(res.pendingToken);
      setNotice(res.message);
      if (!res.ok) setFailure(res.message);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not resend the code.');
    } finally {
      setBusy(false);
      resetCaptcha();
    }
  };

  /* ------------------------------------------------------ forgot password */

  const handleForgot = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailure(null);
    if (!loginId.trim()) {
      setErrors({ loginId: 'Enter your registered email address or mobile number.' });
      return;
    }
    setErrors({});
    if (!guardCaptcha()) return;

    setBusy(true);
    try {
      const res = await api.forgotPassword({
        LoginId: loginId.trim(),
        TurnstileToken: captcha
      });
      setNotice(res.message);
      if (res.pendingToken) {
        setPendingToken(res.pendingToken);
        setMode('reset');
      } else if (!res.ok) {
        setFailure(res.message);
      } else {
        /* The reply is deliberately identical for unknown accounts. */
        setMode('reset');
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not send a reset code.');
    } finally {
      setBusy(false);
      resetCaptcha();
    }
  };

  const handleReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailure(null);
    const next: Record<string, string> = {};
    if (pin.trim().length < 4) next.pin = 'Enter the 6-digit code from your email.';
    if (newPassword.length < 8) next.newPassword = 'Password must be at least 8 characters.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setBusy(true);
    try {
      const res = await api.resetPassword({
        PendingToken: pendingToken,
        Pin: pin.trim(),
        NewPassword: newPassword
      });
      if (res.ok) {
        setMode('signin');
        setPassword('');
        setPin('');
        setNewPassword('');
        setNotice(res.message);
      } else {
        setFailure(res.message || 'Could not change the password.');
      }
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  /* -------------------------------------------------------------- render */

  const backToSignIn = (
    <button
      type="button"
      className="link-button"
      onClick={() => {
        setMode('signin');
        setFailure(null);
        setNotice(null);
        setErrors({});
      }}
    >
      <ArrowLeftIcon size={14} aria-hidden="true" /> Back to sign in
    </button>
  );

  return (
    <div className="auth">
      <aside className="auth-aside">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <StethoscopeIcon size={20} />
          </span>
          <span>
            <span className="brand-name">{INSTITUTION}</span>
            <span className="brand-sub">Institutional Review Board</span>
          </span>
        </div>

        <div>
          <h1 className="auth-headline">Online Research Proposal Submission</h1>
          <p className="auth-lede">
            Submit your proposal to the Research Advisory &amp; Grant Review Committee in
            three guided steps. Your work is saved as you go, so you can return any time
            before final submission.
          </p>
          <ul className="auth-points">
            <li>
              <FileTextIcon size={16} aria-hidden="true" />
              Step 1 — Research Proposal: objectives, methods, timeline and risks.
            </li>
            <li>
              <UsersIcon size={16} aria-hidden="true" />
              Step 2 — Submission: investigators, grant request, HOD and supervisor.
            </li>
            <li>
              <ShieldCheckIcon size={16} aria-hidden="true" />
              Step 3 — Consent: consent forms, proformas and your declaration.
            </li>
          </ul>
        </div>

        <p className="auth-foot">
          Queries:{' '}
          <a href={`mailto:${irbEmail}`} style={{ color: 'var(--blue-200)' }}>
            {irbEmail}
          </a>
        </p>
      </aside>

      <main className="auth-main">
        <div className="auth-card">
          {mode === 'signin' ? (
            <>
              <h2 className="auth-title">Sign in</h2>
              <p className="auth-sub">
                Use the email address or mobile number you registered with.
              </p>
            </>
          ) : mode === 'confirm' ? (
            <>
              <h2 className="auth-title">Confirm your email</h2>
              <p className="auth-sub">Enter the 6-digit code we sent to your inbox.</p>
            </>
          ) : mode === 'forgot' ? (
            <>
              <h2 className="auth-title">Reset your password</h2>
              <p className="auth-sub">
                We will email you a code so you can set a new password.
              </p>
            </>
          ) : (
            <>
              <h2 className="auth-title">Choose a new password</h2>
              <p className="auth-sub">Enter the code from your email and a new password.</p>
            </>
          )}

          {notice ? (
            <div className="notice" style={{ marginTop: 'var(--space-4)' }} role="status">
              <InfoIcon size={18} aria-hidden="true" />
              <span>{notice}</span>
            </div>
          ) : null}

          {failure ? (
            <div
              className="notice notice--error"
              style={{ marginTop: 'var(--space-4)' }}
              role="alert"
            >
              <AlertCircleIcon size={18} aria-hidden="true" />
              <span>{failure}</span>
            </div>
          ) : null}

          {mode === 'signin' ? (
            <form className="auth-form" onSubmit={handleSignIn} noValidate>
              <TextField
                id="signin-login"
                label="Email address or mobile number"
                required
                autoComplete="username"
                placeholder="name@smdc.edu.pk"
                value={loginId}
                onChange={setLoginId}
                error={errors.loginId}
              />
              <TextField
                id="signin-password"
                label="Password"
                required
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={setPassword}
                error={errors.password}
              />

              <Turnstile
                siteKey={siteKey}
                action="login"
                resetSignal={captchaNonce}
                onToken={setCaptcha}
                onUnavailable={setCaptchaBroken}
              />

              <div className="auth-row-between">
                <span />
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setMode('forgot');
                    setFailure(null);
                    setNotice(null);
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="btn btn--primary btn--block"
                style={{ marginTop: 'var(--space-5)' }}
                disabled={busy}
              >
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          ) : null}

          {mode === 'confirm' ? (
            <form className="auth-form" onSubmit={handleConfirm} noValidate>
              <TextField
                id="confirm-pin"
                label="Confirmation code"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={pin}
                onChange={setPin}
                error={errors.pin}
              />
              <Turnstile
                siteKey={siteKey}
                action="resend"
                resetSignal={captchaNonce}
                onToken={setCaptcha}
                onUnavailable={setCaptchaBroken}
              />
              <button
                type="submit"
                className="btn btn--primary btn--block"
                style={{ marginTop: 'var(--space-5)' }}
                disabled={busy}
              >
                {busy ? 'Checking…' : 'Confirm and continue'}
              </button>
              <div className="auth-row-between" style={{ marginTop: 'var(--space-4)' }}>
                {backToSignIn}
                <button type="button" className="link-button" onClick={handleResend} disabled={busy}>
                  Send a new code
                </button>
              </div>
            </form>
          ) : null}

          {mode === 'forgot' ? (
            <form className="auth-form" onSubmit={handleForgot} noValidate>
              <TextField
                id="forgot-login"
                label="Email address or mobile number"
                required
                value={loginId}
                onChange={setLoginId}
                error={errors.loginId}
              />
              <Turnstile
                siteKey={siteKey}
                action="forgot-password"
                resetSignal={captchaNonce}
                onToken={setCaptcha}
                onUnavailable={setCaptchaBroken}
              />
              <button
                type="submit"
                className="btn btn--primary btn--block"
                style={{ marginTop: 'var(--space-5)' }}
                disabled={busy}
              >
                {busy ? 'Sending…' : 'Send reset code'}
              </button>
              <div style={{ marginTop: 'var(--space-4)' }}>{backToSignIn}</div>
            </form>
          ) : null}

          {mode === 'reset' ? (
            <form className="auth-form" onSubmit={handleReset} noValidate>
              <TextField
                id="reset-pin"
                label="Reset code"
                required
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={pin}
                onChange={setPin}
                error={errors.pin}
              />
              <TextField
                id="reset-password"
                label="New password"
                required
                type="password"
                autoComplete="new-password"
                hint="Minimum 8 characters."
                value={newPassword}
                onChange={setNewPassword}
                error={errors.newPassword}
              />
              <button
                type="submit"
                className="btn btn--primary btn--block"
                style={{ marginTop: 'var(--space-5)' }}
                disabled={busy}
              >
                {busy ? 'Saving…' : 'Set new password'}
              </button>
              <div style={{ marginTop: 'var(--space-4)' }}>{backToSignIn}</div>
            </form>
          ) : null}

          {mode === 'signin' ? (
            <p className="auth-alt">
              New researcher? <Link to="/sign-up">Create an account</Link>
            </p>
          ) : null}

          <p className="auth-alt" style={{ marginTop: 'var(--space-3)' }}>
            <CheckCircle2Icon size={13} aria-hidden="true" /> Protected by Cloudflare
            Turnstile.
          </p>
        </div>
      </main>
    </div>
  );
}
